/**
 * CSW Reports (CSW_REPORTS in @nexseed/shared): one endpoint, `/warehouse/csw-reports/:slug`, with
 * the RSW reports' filters plus the CSW-only ones (With SLOC / dates, WH-Bin-Sub-Bin, QC age).
 */
import {
  cswReportSlug,
  type CswReportKey,
  type ReportResult,
} from "@nexseed/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { downloadExport, withQuery } from "@/features/inventory/export";
import { api } from "@/lib/api-client";
import { openPdf } from "@/lib/open-pdf";
import { reportPeriod, reportSearch, type ReportSearch } from "./api";

/** A CSW report's URL filters: the warehouse report filters (api.ts); CSW never sends the Coded age. */
export const cswReportSearch = reportSearch;

export type CswReportSearch = ReportSearch;

export interface CswReportQuery extends CswReportSearch {
  facilityId?: number | undefined;
}

const path = (key: CswReportKey) =>
  `/warehouse/csw-reports/${cswReportSlug(key)}`;

const params = (q: CswReportQuery) => ({
  facilityId: q.facilityId,
  cropId: q.cropId,
  variety: q.variety,
  date: q.date,
  ...reportPeriod(q),
  withSloc: q.withSloc,
  withDates: q.withDates,
  whId: q.whId,
  binId: q.binId,
  subBinId: q.subBinId,
  minDays: q.minDays,
});

export function useCswReport(
  key: CswReportKey,
  q: CswReportQuery,
  enabled = true
) {
  return useQuery({
    queryKey: ["warehouse", "csw-report", key, params(q)],
    queryFn: () => api<ReportResult>(withQuery(path(key), params(q))),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export const cswReportPdf = (key: CswReportKey, q: CswReportQuery) =>
  openPdf(withQuery(path(key), { ...params(q), format: "pdf" }));

export const cswReportXlsx = (key: CswReportKey, q: CswReportQuery) =>
  downloadExport(path(key), { ...params(q), format: "xlsx" }, `${key}.xlsx`);
